<template>
  <div
    v-if="filteredUnreadCheckCount > 0"
    data-testid="checks-unread-panel"
    class="tw-pb-4 tw-px-6 tw-flex tw-flex-col tw-gap-4"
  >
    <RemarkGroup
      v-if="generalUnreadCheckIds.length > 0"
      :name="I18n.t('assistant_check.page.sections.general')"
      :show-mark-as-read="true"
      :disable-mark-as-read="false"
      mark-as-read-heap-tag="assistant-overview-mark-group-as-read"
      @mark-as-read="markGeneralChecksAsRead()"
    >
      <SfAssistantCheck
        v-for="checkId in generalUnreadCheckIds"
        :key="checkId"
        :check="assistantChecks[checkId]"
        :read="checkReadStatus[checkId]"
        :read-by-users="checkReadByUsers[checkId]"
      />
      <template #message-count-indicator>
        {{
          I18n.t('assistant_check.page.checks.count', {
            count: generalUnreadCheckIds.length,
          })
        }}
      </template>
    </RemarkGroup>
    <template v-for="hierarchyItem in unreadHierarchyWithoutGeneral">
      <RemarkGroup
        v-if="
          hierarchyItem?.type === 'workflow' &&
          unreadHierarchyByIdCount.workflows[hierarchyItem.id] > 0
        "
        :key="hierarchyItem.id"
        :name="workflowById[hierarchyItem.id].name"
        :show-mark-as-read="true"
        :disable-mark-as-read="false"
        mark-as-read-heap-tag="assistant-overview-mark-group-as-read"
        @mark-as-read="markGroupedChecksAsRead(hierarchyItem.id)"
      >
        <template v-for="context in hierarchyItem.children">
          <template v-for="check in context.children">
            <SfAssistantCheck
              v-if="!checkReadStatus[check.id]"
              :key="check.id"
              :check="assistantChecks[check.id]"
              :read="checkReadStatus[check.id]"
              :read-by-users="checkReadByUsers[check.id]"
            />
          </template>
        </template>
        <template #message-count-indicator>
          {{
            I18n.t('assistant_check.page.checks.count', {
              count: unreadHierarchyByIdCount.workflows[hierarchyItem.id],
            })
          }}
        </template>
      </RemarkGroup>
    </template>
    <div
      class="
        tw-mx-auto
        tw-my-4
        tw-font-medium
        tw-text-sm
        tw-text-sf-gray-light
        tw-text-center
      "
    >
      <SfButtonInverted
        v-if="hasUnreadChecks"
        small
        inline
        variant="dark"
        :disabled="false"
        data-heap="assistant-overview-mark-all-as-read"
        @click="markAllAsRead"
      >
        <span
          data-testid="checks-mark-all-as-read"
          class="tw-flex tw-flex-row tw-items-center tw-align-baseline tw-gap-2"
        >
          <i class="fa fa-envelope-open" />
          {{ I18n.t('communication.inbox.mark_all_as_read') }}
        </span>
      </SfButtonInverted>
    </div>
  </div>
  <div
    v-else
    class="
      tw-h-full tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center
    "
  >
    <span class="tw-font-medium tw-text-2xl">
      {{ I18n.t('assistant_check.page.no_unread') }}
    </span>
  </div>
</template>

<script setup lang="ts">
import useI18n from '@app/shared/vue/composables/use-I18n'
import { storeToRefs } from 'pinia'
import RemarkGroup from '@app/application/communication/screens/inbox/components/remark-group.vue'
import SfAssistantCheck from '@app/application/sf-assistant-checks/components/sf-assistant-check.vue'
import SfButtonInverted from '@app/shared/vue/components/sf-button/sf-button-inverted.vue'
import { useSfAssistantChecksPage } from '../stores/page'
import { computed } from 'vue'
import { useAssistantChecksStore } from '@app/application/sf-assistant-checks/stores/assistant-checks'

const I18n = useI18n()
const sfAssistantChecksPageStore = useSfAssistantChecksPage()
const assistantChecksStore = useAssistantChecksStore()
const { assistantChecks } = storeToRefs(assistantChecksStore)
assistantChecksStore.initFromPageStore()

const {
  checkReadStatus,
  checkReadByUsers,
  unreadCheckIds,
  filteredUnreadCheckCount,
  generalUnreadCheckIds,
  workflowById,
  unreadHierarchyTree,
  unreadHierarchyByIdCount,
} = storeToRefs(sfAssistantChecksPageStore)

const hasUnreadChecks = computed(() => unreadCheckIds.value.length > 0)

const unreadHierarchyArray = computed(() => {
  const hierarchyArray = unreadHierarchyTree.value?.unwrapDeep(true)

  if (!hierarchyArray || hierarchyArray.length === 0 || !hierarchyArray[0]) {
    return []
  }

  if ('children' in hierarchyArray[0]) {
    return hierarchyArray[0].children
  }

  return []
})

const unreadHierarchyWithoutGeneral = computed(() =>
  unreadHierarchyArray.value?.filter((workflow) => workflow.id !== 'general')
)

const markGeneralChecksAsRead = (): void => {
  tryToMarkAsRead(() =>
    sfAssistantChecksPageStore.markChecksAsRead(generalUnreadCheckIds.value)
  )
}

const markGroupedChecksAsRead = (workflowId: string): void => {
  const workflow = unreadHierarchyArray.value?.find(
    (workflow) => workflow?.id === workflowId
  )

  if (!workflow) {
    return
  }

  const checkIds =
    workflow.id === 'general'
      ? generalUnreadCheckIds.value
      : workflow.children?.reduce((workflowAcc, context) => {
          if (!context.children) {
            return workflowAcc
          }

          const checkIds = context.children.reduce((contextAcc, check) => {
            return contextAcc.concat(check.id)
          }, [] as string[])

          return workflowAcc.concat(checkIds)
        }, [] as string[])

  if (!checkIds || checkIds.length === 0) {
    return
  }

  tryToMarkAsRead(() => sfAssistantChecksPageStore.markChecksAsRead(checkIds))
}

const markAllAsRead = (): void => {
  tryToMarkAsRead(() =>
    sfAssistantChecksPageStore.markChecksAsRead(unreadCheckIds.value)
  )
}
const tryToMarkAsRead = (markAsReadCallback: () => void): void => {
  try {
    markAsReadCallback()
  } catch {
    sfAssistantChecksPageStore.fetchAll()
  }
}
</script>

<style scoped>
.remark-group {
  --sf-remark-group-sticky-offset: 16px;
}
</style>
